import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../services/modal.service';
import { Relay } from '../../../models/relay.model';

export interface RelayFormResult {
  name: string;
  ipAddress: string;
  port: number;
  password: string | null;
  pollingIntervalMs: number;
}

const PORT_MIN = 1;
const PORT_MAX = 65535;
const POLLING_MIN_MS = 1000;

@Component({
  selector: 'app-relay-form-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './relay-form-modal.html',
  styleUrl: './relay-form-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelayFormModalComponent implements OnInit {
  @Input() relay?: Relay;

  private fb = inject(FormBuilder);
  private modalService = inject(ModalService);

  showPassword = false;

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, nonEmptyTrimmed]],
    ipAddress: [
      '',
      [Validators.required, Validators.pattern(/^\d{1,3}(\.\d{1,3}){3}$/)],
    ],
    port: [
      502,
      [Validators.required, Validators.min(PORT_MIN), Validators.max(PORT_MAX)],
    ],
    password: [''],
    pollingIntervalMs: [
      60000,
      [Validators.required, Validators.min(POLLING_MIN_MS)],
    ],
  });

  get isEdit(): boolean {
    return !!this.relay;
  }

  ngOnInit(): void {
    if (this.isEdit && this.relay) {
      this.form.patchValue(
        {
          name: this.relay.name,
          ipAddress: this.relay.ipAddress,
          port: this.relay.port,
          password: this.relay.password ?? '',
          pollingIntervalMs: this.relay.pollingIntervalMs,
        },
        { emitEvent: false },
      );
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  isInvalid(
    controlName: 'name' | 'ipAddress' | 'port' | 'pollingIntervalMs',
  ): boolean {
    const c = this.form.get(controlName);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;
    const passwordValue = (v.password as string | null)?.trim() ?? '';

    const result: RelayFormResult = {
      name: (v.name as string).trim(),
      ipAddress: (v.ipAddress as string).trim(),
      port: Number(v.port),
      password: passwordValue.length > 0 ? passwordValue : null,
      pollingIntervalMs: Number(v.pollingIntervalMs),
    };

    this.modalService.close(result);
  }

  onCancel(): void {
    this.modalService.close();
  }
}

function nonEmptyTrimmed(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '') as string;
  return value.trim().length > 0 ? null : { required: true };
}
